import {
  ComponentStatus,
  ComponentTemplateDto,
  LibraryPrivacy,
} from "@growth-machine-llc/stridist-api-client";
import { MoreHoriz } from "@mui/icons-material";
import { IconButton, MenuProps } from "@mui/material";
import React from "react";
import { ConfirmActionDialog } from "../dialog/ConfirmActionDialog";
import { useDeleteComponentTemplateLibraryMutation } from "./hooks/useDeleteLibraryComponentTempleteLibrary";
import { useComponentTemplateListFilterContext } from "../../contexts/ComponentTemplateLibraryFilterContext";
import { TeamRole } from "../../constants";
import { useToggleComponentTemplateVisibilityMutation } from "./hooks/useToggleTeamComponentTemplateVisibility";
import {
  UserMinus as UnshareIcon,
  Trash as DeleteIcon,
  Share2 as ShareIcon,
  Pencil as EditIcon,
  Archive as ArchiveIcon,
  ArchiveRestore as UnarchiveIcon,
} from "lucide-react";
import { ListMenu, MenuOption } from "../menu/ListMenu";
import { useArchiveLibraryComponentTemplateMutation } from "./hooks/useArchiveLibraryComponentTemplate";
import { useRestoreLibraryComponentTemplateMutation } from "./hooks/useRestoreLibraryComponentTemplate";

export interface ComponentTemplateMenuProps
  extends Omit<MenuProps, "open" | "onClose" | "component"> {
  component: ComponentTemplateDto;
  className?: string;
  onEdit?: () => void;
  isTeamAccount: boolean;
  userTeamRole?: TeamRole;
  userId: number;
}

export const ComponentTemplateMenu = (props: ComponentTemplateMenuProps) => {
  const {
    component,
    className,
    onEdit,
    isTeamAccount,
    userTeamRole,
    userId,
    ...other
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const status = component.archived
    ? ComponentStatus.ARCHIVED
    : ComponentStatus.PUBLISHED;

  const isTeamShared = component.privacy === LibraryPrivacy.TEAM;
  const isTemplateOwner = userId === component.coachId;

  const { privacy } = useComponentTemplateListFilterContext();

  const canShare =
    isTeamAccount && (userTeamRole === TeamRole.OWNER || isTemplateOwner);

  const { mutate: removeTemplate } =
    useDeleteComponentTemplateLibraryMutation();

  const { mutate: toggleComponentTemplatePrivacy } =
    useToggleComponentTemplateVisibilityMutation(privacy);

  const { mutate: archiveTemplate } =
    useArchiveLibraryComponentTemplateMutation();

  const { mutate: restoreTemplate } =
    useRestoreLibraryComponentTemplateMutation();

  const [
    confirmComponentTemplateDeleteDialogOpen,
    setConfirmComponentTemplateDeleteDialogOpen,
  ] = React.useState(false);

  const handleOpen = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.stopPropagation();
      event.preventDefault();
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleEdit = React.useCallback(() => {
    if (onEdit) {
      onEdit();
    }
    setAnchorEl(null);
  }, [onEdit]);

  const handleClose = React.useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleComponentDelete = React.useCallback(() => {
    setAnchorEl(null);

    // TODO temporary disable confirmation to speed up delete actions
    setConfirmComponentTemplateDeleteDialogOpen(true);
  }, [component, setConfirmComponentTemplateDeleteDialogOpen]);

  const handleConfirmComponentTemplateDelete = React.useCallback(() => {
    removeTemplate(component.id);
    setConfirmComponentTemplateDeleteDialogOpen(false);
  }, [component]);

  const handleCancelComponentTemplateDelete = React.useCallback(() => {
    setConfirmComponentTemplateDeleteDialogOpen(false);
  }, []);

  const handleToggleComponentTemplatePrivacy = React.useCallback(() => {
    setAnchorEl(null);
    toggleComponentTemplatePrivacy({
      id: component.id,
      isTeamShared: !isTeamShared,
    });
  }, [component, toggleComponentTemplatePrivacy]);

  const handleArchiveRestoreComponentTemplate = React.useCallback(() => {
    setAnchorEl(null);
    if (component.archived) {
      restoreTemplate(component.id);
    } else {
      archiveTemplate(component.id);
    }
  }, [component, archiveTemplate, restoreTemplate]);

  const menuOptions: MenuOption[] = [
    {
      label: "Edit template",
      onClick: handleEdit,
      visible: isTemplateOwner,
      icon: <EditIcon />,
    },
    {
      label: component.archived ? "Restore template" : "Archive template",
      onClick: handleArchiveRestoreComponentTemplate,
      visible: isTemplateOwner,
      icon: component.archived ? <UnarchiveIcon /> : <ArchiveIcon />,
    },
    {
      label: "Delete template",
      onClick: handleComponentDelete,
      visible: component.archived && isTemplateOwner,
      icon: <DeleteIcon />,
    },

    {
      label: isTeamShared ? "Remove from Team" : "Share with Team",
      onClick: handleToggleComponentTemplatePrivacy,
      visible: !component.archived && canShare,
      icon: isTeamShared ? <UnshareIcon /> : <ShareIcon />,
    },
  ];

  return (
    <>
      <IconButton onClick={handleOpen} size="large">
        <MoreHoriz />
      </IconButton>
      <ListMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        options={menuOptions.filter(({ visible }) => visible)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        className={className}
        {...other}
      />
      <ConfirmActionDialog
        title={"Are you sure you want to delete component template?"}
        open={confirmComponentTemplateDeleteDialogOpen}
        onClose={handleCancelComponentTemplateDelete}
        onCancel={handleCancelComponentTemplateDelete}
        onConfirm={handleConfirmComponentTemplateDelete}
      />
    </>
  );
};
